import { useMemo } from 'react';

import {
	useStarterDashboardV2BreakpointState,
	useStarterDashboardV2PageData,
	useStarterDashboardV2TabsStateContext,
} from 'src/app/pages/starterDashboardV2/pageContext';

import type { ActionButtonProps } from '../../types';

export type UsePageHeaderReturn = ModelHookResult<
	{
		title: string;
		imageUrl?: string;
		actionButtonsOrientation: 'ltr' | 'rtl';
		actionButtons: Array<ActionButtonProps['type']>;
		headerStyle?: string;
		containerStyle?: string;
		mobile: boolean;
	},
	undefined
>;

export const SECONDARY_ACTION_BUTTONS = ['edit', 'view', 'link'] as const;
export const STYLES = {
	header: {
		base: 'overflow-hidden flex w-full max-w-full items-center justify-center border-b border-t border-[var(--stroke-weaker-color,#E1DDE9)] bg-[var(--background-base-color,#FFF)]',
		desktop: 'max-h-[72px] px-[var(--space-7,28px)] py-[var(--space-3,12px)] left-0 right-0 top-0 z-10',
		mobile: 'px-[var(--space-4,16px)] py-[var(--space-2,8px)]',
	},
	container: {
		base: 'flex w-full flex-shrink-0 grow basis-0 items-center gap-[var(--space-4,16px)] sm:max-w-full md:max-w-[1216px]',
		desktop: 'flex-row',
		mobile: 'flex-col',
	},
};

const useData = () => {
	const {
		data: {
			petition: { ask, photo },
			viewer: {
				experiments: {
					starterExperienceDashboardSunriseV1: { variation },
				},
			},
		},
	} = useStarterDashboardV2PageData();

	return { ask, photo, variation };
};

export function usePageHeader(): UsePageHeaderReturn {
	const { ask, photo, variation } = useData();
	const { isMobile } = useStarterDashboardV2BreakpointState();
	const imageUrl = useMemo(
		() =>
			photo?.petitionSmall.url && !photo.petitionSmall.url.includes('#processing')
				? photo.petitionSmall.url
				: undefined,
		[photo?.petitionSmall.url],
	);

	const shareButtonType = variation === 'variant2' ? 'shareVariant' : 'share';

	const {
		data: { selectedTab },
	} = useStarterDashboardV2TabsStateContext();
	const actionButtons: Array<ActionButtonProps['type']> = useMemo(() => {
		if (!isMobile || selectedTab === 'home') {
			return [...SECONDARY_ACTION_BUTTONS, shareButtonType];
		}
		if (selectedTab === 'share') {
			return [shareButtonType];
		}
		return [];
	}, [isMobile, selectedTab, shareButtonType]);
	const headerStyle = useMemo(
		() =>
			`${STYLES.header.base} ${isMobile && actionButtons.length > 1 ? STYLES.header.mobile : STYLES.header.desktop}${isMobile ? '' : ' sticky'}`,
		[isMobile, actionButtons],
	);
	const containerStyle = useMemo(
		() =>
			`${STYLES.container.base} ${isMobile && actionButtons.length > 1 ? STYLES.container.mobile : STYLES.container.desktop}`,
		[isMobile, actionButtons],
	);

	return {
		data: {
			title: ask,
			imageUrl,
			actionButtonsOrientation: isMobile ? 'rtl' : 'ltr',
			actionButtons,
			headerStyle,
			containerStyle,
			mobile: isMobile && actionButtons.length > 1,
		},
	};
}
